import type { ReactNode } from 'react';
import { IconCalendarOff, IconChevronRight } from '@tabler/icons-react';
import type { RidesEventView } from '@actschurch/ccms-sdk';
import { formatTime, monthLabel, parseApiDate } from '@/lib/format';
import { useCopy } from '@/lib/settings';
import { PickerCard, PickerGroup } from '@/components/ui/PickerCard';
import { EmptyState } from '@/components/ui/EmptyState';
import '@/styles/flows.css';

interface Props {
  events: RidesEventView[];
  /** single-select */
  value?: number | null;
  onSelect?: (eventId: number) => void;
  /** multi-select */
  values?: number[];
  onToggle?: (eventId: number) => void;
  /** Show how many drivers are offering lifts (rider view). */
  showDriverCount?: boolean;
  /** The one next step under the empty state (e.g. "Back to Home"). */
  emptyAction?: ReactNode;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "18:30", "Several service times" (Sunday services) or "" when the time isn't known. */
export function eventTimeLabel(e: RidesEventView): string {
  if (e.usesCampusServiceTimes) return 'Several service times';
  if (e.defaultStartTime) return formatTime(e.defaultStartTime);
  return '';
}

/** Date tile: weekday over the day of the month ("Sun" / "11"). The month is the group heading. */
export function DateTile({ date }: { date: string | number }) {
  const d = parseApiDate(date);
  return (
    <span className="rides-datetile" aria-hidden="true">
      <span className="wd">{d ? WEEKDAYS[d.getDay()] : ''}</span>
      <span className="d">{d ? d.getDate() : ''}</span>
    </span>
  );
}

function describe(e: RidesEventView, showDriverCount?: boolean): ReactNode {
  const time = eventTimeLabel(e) || 'Time to be confirmed';
  const where = e.locationName ? ` · ${e.locationName}` : '';
  const extra = e.description?.trim() && e.description.trim() !== e.name ? e.description.trim() : e.typeName && !e.name.toLowerCase().includes(e.typeName.toLowerCase()) ? e.typeName : '';
  const count = e.driverCount ?? 0;
  return (
    <>
      {time}
      {where}
      {extra && <><br />{extra}</>}
      {showDriverCount && (
        <span className={`rides-count${count ? '' : ' none'}`}>{count ? `${count} ${count === 1 ? 'driver' : 'drivers'} offering lifts` : 'No drivers yet'}</span>
      )}
    </>
  );
}

/**
 * Upcoming services and events, grouped by month. Each is one big card: date tile, name, time and
 * place, and (for riders) how many drivers are offering lifts. Riders pick one; drivers tick several.
 */
export function EventPicker({ events, value, onSelect, values, onToggle, showDriverCount, emptyAction }: Props) {
  const { t } = useCopy();
  const multi = !!onToggle;
  const sorted = [...events].filter((e) => e.showInRides !== false).sort((a, b) => a.eventDate - b.eventDate);
  if (!sorted.length) {
    return (
      <EmptyState icon={<IconCalendarOff size={28} />} title="No services open for lifts yet" action={emptyAction}>
        {t('ride.events_empty')}
      </EmptyState>
    );
  }

  const months: { label: string; items: RidesEventView[] }[] = [];
  for (const e of sorted) {
    const m = monthLabel(e.date);
    const last = months[months.length - 1];
    if (last && last.label === m) last.items.push(e);
    else months.push({ label: m, items: [e] });
  }

  return (
    <div className="rides-flow-stack">
      {months.map((m) => (
        <PickerGroup key={m.label} label={m.label} multi={multi}>
          {m.items.map((e) => {
            const selected = multi ? (values ?? []).includes(e.eventID) : value === e.eventID;
            return (
              <PickerCard
                key={e.eventID}
                multi={multi}
                selected={selected}
                hideIndicator={!multi}
                onSelect={() => (multi ? onToggle?.(e.eventID) : onSelect?.(e.eventID))}
                media={<DateTile date={e.date} />}
                title={e.name}
                description={describe(e, showDriverCount && !multi)}
                trailing={!multi ? <span className="rides-chev" aria-hidden="true"><IconChevronRight size={22} /></span> : undefined}
              />
            );
          })}
        </PickerGroup>
      ))}
    </div>
  );
}
